import type { StoredReview } from '../../api/blog';

const SEVERITY_BADGE = {
  blocker: 'badge badge--danger',
  major: 'badge badge--warning',
  minor: 'badge',
} as const;

/**
 * Why a generated article was published, or why it was not.
 *
 * Two verdicts, kept apart on purpose. What the machine found is a fact — this figure is not in
 * the parameter set, this phrase is on the advice list — and is shown first. What the reviewing
 * model thought is an opinion, and is shown as one. Somebody deciding whether to publish a parked
 * article needs to know which kind of objection they are overruling.
 */
export default function ReviewCard({ review }: { review: StoredReview }) {
  const found = review.deterministic;

  return (
    <div className="review">
      <p className="review__headline">
        {review.score !== null ? (
          <>
            Review score <strong>{review.score}</strong>
            {review.verdict ? ` · ${review.verdict}` : ''}
          </>
        ) : (
          'Not reviewed by the model'
        )}
        {review.revised && <span className="muted"> · revised once</span>}
        <span className="muted"> · {found.wordCount} words</span>
      </p>

      {found.blockers.length > 0 && (
        <div className="alert alert--critical">
          <strong>Automated checks that stopped it</strong>
          <ul>
            {found.blockers.map((blocker) => (
              <li key={blocker}>{blocker}</li>
            ))}
          </ul>
        </div>
      )}

      {review.revisionReasons && review.revisionReasons.length > 0 && (
        <details open={found.blockers.length === 0}>
          <summary>What the first draft was sent back for ({review.revisionReasons.length})</summary>
          <ul>
            {review.revisionReasons.map((reason, index) => (
              <li key={index}>{reason}</li>
            ))}
          </ul>
        </details>
      )}

      {review.issues.length > 0 && (
        <>
          <h4>Reviewer's issues</h4>
          <ul className="review__issues">
            {review.issues.map((issue, index) => (
              <li key={index}>
                <span className={SEVERITY_BADGE[issue.severity] ?? 'badge'}>{issue.severity}</span> {issue.problem}
                {issue.quote && <blockquote>{issue.quote}</blockquote>}
                {issue.fix && <p className="muted">Fix: {issue.fix}</p>}
              </li>
            ))}
          </ul>
        </>
      )}

      {review.checks.length > 0 && (
        <details>
          <summary>
            Checklist — {review.checks.filter((check) => check.pass).length} of {review.checks.length} passed
          </summary>
          <ul className="review__checks">
            {review.checks.map((check) => (
              <li key={check.id}>
                <span className={check.pass ? 'badge badge--published' : 'badge badge--danger'}>{check.pass ? 'pass' : 'fail'}</span>{' '}
                <code>{check.id}</code> <span className="muted">{check.note}</span>
              </li>
            ))}
          </ul>
        </details>
      )}

      {(found.droppedSources.length > 0 || found.unlinkedTargets.length > 0 || found.unknownTags.length > 0) && (
        <details>
          <summary>Repaired without asking</summary>
          <ul>
            {found.droppedSources.map((url) => (
              <li key={`s-${url}`}>
                Source dropped, not an official domain: <code>{url}</code>
              </li>
            ))}
            {found.unlinkedTargets.map((target, index) => (
              <li key={`l-${index}`}>
                Link removed, text kept: <code>{target}</code>
              </li>
            ))}
            {found.unknownTags.map((tag) => (
              <li key={`t-${tag}`}>
                Tag dropped, not in the vocabulary: <code>{tag}</code>
              </li>
            ))}
          </ul>
        </details>
      )}
    </div>
  );
}
